// Comprehensive Spanish Terms and Conditions for Solunary Platform.
// Reflects platform role as Merchant of Record, wellness records disclaimer,
// AI human-in-the-loop requirement, and multi-tenant practice architecture.
export default {
  lastUpdatedLabel: "Última actualización",
  lastUpdatedDate: "1 de octubre de 2026",
  effectiveDate: "1 de octubre de 2026",
  version: "2.4",
  sections: [
    {
      id: "acceptance",
      heading: "1. Aceptación de los términos y elegibilidad",
      category: "General",
      paragraphs: [
        "Al crear una cuenta, acceder o utilizar la plataforma Solunary (\"Solunary\", \"la Plataforma\", \"nosotros\" o \"nuestro\"), reconoces haber leído, entendido y aceptado quedar vinculado por estos Términos y Condiciones (\"Términos\"). Si no estás de acuerdo con estos Términos, no debes acceder ni utilizar la Plataforma.",
        "Declaras y garantizas que tienes al menos 18 años de edad (o la mayoría de edad legal en tu jurisdicción) y que posees la plena capacidad jurídica para celebrar un contrato vinculante. Si aceptas estos Términos en nombre de una empresa, consulta u otra entidad jurídica, declaras tener la autoridad necesaria para vincular a dicha entidad a estos Términos.",
      ],
    },
    {
      id: "service-description",
      heading: "2. Descripción de la plataforma y arquitectura de Solunary",
      category: "Plataforma",
      paragraphs: [
        "Solunary proporciona un sistema operativo en la nube unificado diseñado específicamente para profesionales, terapeutas y educadores en salud complementaria, integrativa y bienestar. La Plataforma comprende:",
        "• Gestión de consulta (☀ Practice): seguimiento de clientes, reservas online, gestión de calendario, cuestionarios de intake, facturación y CRM.",
        "• Academia y educación (☾ Academy): creación de cursos digitales, reproductor de vídeo y audio, rutas de bienestar guiadas, membresías y círculos comunitarios privados.",
        "• Herramientas de inteligencia (✦ Solunary AI): asistencia administrativa para el profesional, estructuración de notas de sesión, síntesis de cuestionarios y comunicaciones automatizadas.",
        "• Teleconsulta integrada: salas de videoconsulta seguras con tarificación por minuto o por sesión.",
        "• Tienda y marketplace: catálogo de productos digitales, bonos de sesiones, tarjetas de regalo y descubrimiento de profesionales.",
        "Solunary mejora y evoluciona la Plataforma de forma continua. Nos reservamos el derecho de modificar, actualizar o discontinuar funciones con un aviso razonable siempre que sea viable.",
      ],
    },
    {
      id: "accounts-responsibilities",
      heading: "3. Cuentas, credenciales y autorizaciones profesionales",
      category: "Plataforma",
      paragraphs: [
        "Para acceder a las funciones de administración de Solunary, debes registrarte. Te comprometes a proporcionar información veraz, actual y completa durante el registro y a mantenerla debidamente actualizada.",
        "Eres el único responsable de preservar la confidencialidad de tus credenciales de acceso y de proteger tus dispositivos. Asumes la responsabilidad de todas las actividades realizadas desde tu cuenta. Debes notificarnos de inmediato a hey@solunary.health si detectas cualquier acceso no autorizado o incidente de seguridad.",
        "Los profesionales declaran y garantizan que cuentan con todas las titulaciones, acreditaciones, seguros y autorizaciones profesionales exigidas por la legislación aplicable en sus respectivas jurisdicciones para ofrecer sus servicios de bienestar o terapias.",
      ],
    },
    {
      id: "wellness-records",
      heading: "4. Registros de bienestar y exención de responsabilidad médica",
      category: "Salud y Deontología",
      callout: {
        type: "warning",
        title: "Aviso Médico Fundamental",
        body: "Solunary es un software de gestión de bienestar y NO es un Registro Médico Electrónico (EMR/EHR) certificado ni un centro sanitario. Solunary no proporciona diagnósticos ni tratamientos médicos, y no establece una relación médico-paciente.",
      },
      paragraphs: [
        "Solunary proporciona herramientas de gestión de registros de bienestar para facilitar la organización, reservas y seguimiento de clientes de profesionales holísticos. Solunary no es un dispositivo médico y no presta servicios clínicos.",
        "Los profesionales conservan la responsabilidad clínica, técnica y regulatoria exclusiva de todos los tratamientos, recomendaciones y servicios prestados a sus clientes, así como del cumplimiento de las leyes de salud aplicables.",
        "URGENCIAS MÉDICAS: La Plataforma no está diseñada para situaciones de emergencia médica. Ante cualquier urgencia de salud, los clientes deben contactar inmediatamente a los servicios de emergencia (112 en Europa, 911 en Norteamérica) o acudir al centro médico más cercano.",
      ],
    },
    {
      id: "ai-content",
      heading: "5. Herramientas de Inteligencia Artificial Solunary AI",
      category: "Inteligencia e IA",
      callout: {
        type: "info",
        title: "Supervisión Humana Obligatoria",
        body: "Todos los resultados, resúmenes y sugerencias generados por IA son propuestas preliminares para la revisión exclusiva del profesional. Ningún contenido generado por IA debe enviarse a un cliente sin verificación y aprobación humana previa.",
      },
      paragraphs: [
        "Solunary incorpora funciones de inteligencia artificial (✦ Solunary AI) concebidas como apoyo en la redacción de notas, organización de cuestionarios y preparación de comunicaciones.",
        "Todas las sugerencias generadas por la IA son borradores. El profesional debe verificar la exactitud e idoneidad clínica de cualquier información antes de compartirla con un cliente o incorporarla a los registros.",
        "Solunary no asume responsabilidad alguna sobre la exactitud o idoneidad de los contenidos generados por IA, ni por las decisiones adoptadas a partir de contenidos de IA no verificados.",
      ],
    },
    {
      id: "payments",
      heading: "6. Facturación, Comerciante de registro y Pasarela Stripe",
      category: "Facturación y Finanzas",
      callout: {
        type: "info",
        title: "Modelo de Comerciante de Registro",
        body: "Solunary actúa como comerciante de registro (Merchant of Record) para los cobros efectuados en la Plataforma. Las cuentas Stripe Connect de los profesionales actúan como destino de transferencias mediante nuestro libro contable de doble entrada.",
      },
      paragraphs: [
        "Comerciante de Registro: Solunary actúa como comerciante de registro (Merchant of Record) para los cobros efectuados en la Plataforma (citas, teleconsultas, cursos, productos y tarjetas de regalo). Todos los pagos se procesan a través de la infraestructura de Stripe de la plataforma.",
        "Liquidaciones a profesionales: Las cuentas Stripe Connect de los profesionales actúan como destino de transferencias (payouts). Los fondos se acreditan en el libro de doble entrada de Solunary y se transfieren tras descontar la comisión de la plataforma y gastos aplicables.",
        "Las comisiones se aplican según la tabla vigente. Las transferencias a las cuentas de los profesionales se realizan de forma periódica tras el período de retención estándar (habitualmente 7 días para cobertura de disputas).",
        "Suscripciones SaaS: Los planes de suscripción de los profesionales se facturan mensual o anualmente y se renuevan de manera automática salvo cancelación previa. Las cuotas no son reembolsables salvo imperativo legal.",
      ],
    },
    {
      id: "telehealth",
      heading: "7. Teleconsultas y Videollamadas Seguras",
      category: "Teleconsulta",
      paragraphs: [
        "Solunary integra infraestructura de videoconsulta segura (mediante Daily.co), con opción de cobro por sesión o por minuto.",
        "Los clientes reciben un desglose claro de tarifas antes de acceder a la sala de consulta. Temporizadores automáticos cierran las salas cuando finaliza el tiempo contratado.",
        "Cada usuario es responsable de contar con dispositivos y conexión a internet adecuados. Solunary no asume responsabilidad por cortes atribuibles a la conexión de los usuarios.",
        "Queda expresamente prohibida la grabación de las sesiones sin el consentimiento mutuo e informado de todos los intervinientes.",
      ],
    },
    {
      id: "marketplace-courses",
      heading: "8. Cursos digitales, Programas y Tienda",
      category: "Academia y Venta",
      paragraphs: [
        "Los profesionales que publican cursos conceden a los compradores una licencia personal, no exclusiva e intransferible para el acceso a los contenidos formativos con fines de desarrollo personal.",
        "Todos los materiales didácticos, vídeos y textos son propiedad intelectual del profesional creador. Queda prohibida su reventa o distribución sin autorización previa.",
        "Solunary facilita el soporte técnico y el procesamiento de pagos, sin avalar el contenido específico de las formaciones de terceros.",
      ],
    },
    {
      id: "client-bookings",
      heading: "9. Reservas de citas, Cambios y Cancelaciones",
      category: "Reservas",
      paragraphs: [
        "Las reservas de citas realizadas a través de Solunary se rigen por estos Términos y por las condiciones particulares establecidas por cada profesional (plazos de cancelación, depósitos y penalizaciones).",
        "Los clientes pueden reprogramar o cancelar citas respetando los plazos fijados por la consulta. Las cancelaciones fuera de plazo o incomparecencias pueden conllevar la pérdida del anticipo o cargos según la política de la consulta.",
        "El profesional se reserva el derecho de modificar una cita por motivos justificados o imprevistos, avisando puntualmente al cliente.",
      ],
    },
    {
      id: "user-content",
      heading: "10. Propiedad intelectual y Contenidos del usuario",
      category: "Legal y Propiedad",
      paragraphs: [
        "Propiedad de los contenidos: Conservas la titularidad y los derechos de propiedad intelectual sobre los datos, marcas, cursos, materiales y notas que cargues en la Plataforma.",
        "Licencia a Solunary: Concedes a Solunary una licencia limitada para alojar, reproducir y transmitir tus contenidos con el único fin de prestar los servicios de la plataforma.",
        "Propiedad de Solunary: La marca Solunary, sus códigos, diseños de interfaz y software son propiedad exclusiva de Solunary y están protegidos por las leyes de propiedad intelectual.",
      ],
    },
    {
      id: "acceptable-use",
      heading: "11. Uso aceptable y Conductas prohibidas",
      category: "Legal y Propiedad",
      paragraphs: [
        "Te comprometes a no utilizar la Plataforma para: (a) infringir leyes o normativas aplicables; (b) ejercer ilegalmente la medicina o profesiones sanitarias reguladas; (c) publicar afirmaciones de salud engañosas o fraudulentas; (d) acosar, difamar o amenazar a terceros; (e) introducir virus o programas maliciosos; (f) realizar raspado o extracción no autorizada de datos; o (g) vulnerar las medidas de seguridad y cifrado de la Plataforma.",
        "Solunary se reserva la facultad de suspender o cancelar cuentas que incumplan estas directrices.",
      ],
    },
    {
      id: "third-party",
      heading: "12. Servicios e Integraciones de terceros",
      category: "Integraciones",
      paragraphs: [
        "La Plataforma se integra con proveedores externos como Stripe para pagos, Daily.co para videoconsultas, Resend para envío de correos y Google para sincronización de calendarios.",
        "El uso de dichas integraciones está sujeto a los términos de cada proveedor. Solunary no se responsabiliza de interrupciones o cambios de servicio de estos proveedores.",
      ],
    },
    {
      id: "privacy-data",
      heading: "13. Protección de datos y Confidencialidad",
      category: "Datos y Privacidad",
      paragraphs: [
        "El tratamiento de datos personales se rige por nuestra Política de Privacidad (/legal/privacy). Al utilizar la Plataforma, aceptas los tratamientos descritos en la misma.",
        "Respecto a los datos de los clientes del profesional, Solunary actúa como encargado del tratamiento y el profesional como responsable del tratamiento conforme al RGPD. Solunary aplica medidas de seguridad técnicas y organizativas para preservar la confidencialidad de la información.",
      ],
    },
    {
      id: "termination",
      heading: "14. Duración, Cancelación y Suspensión",
      category: "General",
      paragraphs: [
        "Puedes cancelar tu suscripción en cualquier momento desde los ajustes de tu cuenta. La cancelación surtirá efecto al finalizar el ciclo de facturación vigente.",
        "Solunary podrá suspender o dar de baja una cuenta en caso de incumplimiento grave de estos Términos, fraude o impago reiterado tras aviso previo.",
        "Tras la cancelación, dispondrás de un plazo de 30 días para exportar los datos de tus clientes y facturas antes de su eliminación conforme a la política de retención.",
      ],
    },
    {
      id: "limitation-of-liability",
      heading: "15. Exclusión de garantías y Limitación de responsabilidad",
      category: "Responsabilidad",
      paragraphs: [
        "LA PLATAFORMA SE PROPORCIONA «TAL CUAL» Y «SEGÚN DISPONIBILIDAD», SIN GARANTÍAS DE NINGÚN TIPO, EXPRESAS O IMPLÍCITAS, INCLUIDAS LAS DE COMERCIABILIDAD O IDONEIDAD PARA UN FIN DETERMINADO.",
        "EN LA MÁXIMA MEDIDA PERMITIDA POR LA LEY, SOLUNARY NO SERÁ RESPONSABLE DE DAÑOS INDIRECTOS, LUCRO CESANTE, PÉRDIDA DE DATOS O REPUTACIÓN RELACIONADOS CON EL USO DE LA PLATAFORMA.",
        "LA RESPONSABILIDAD TOTAL DE SOLUNARY QUEDARÁ LIMITADA A LAS CANTIDADES EFECTIVAMENTE ABONADAS POR EL USUARIO A SOLUNARY EN LOS DOCE (12) MESES ANTERIORES AL HECHO CAUSANTE.",
      ],
    },
    {
      id: "indemnification",
      heading: "16. Indemnización",
      category: "Responsabilidad",
      paragraphs: [
        "Aceptas defender, indemnizar y mantener indemne a Solunary, sus directivos, empleados y filiales frente a cualquier reclamación, daño, pérdida o gasto (incluidos honorarios legales) derivados de: (a) tu uso de la Plataforma; (b) el incumplimiento de estos Términos; (c) los servicios o consejos prestados a tus clientes; o (d) la infracción de derechos de terceros.",
      ],
    },
    {
      id: "dispute-resolution",
      heading: "17. Ley aplicable y Resolución de conflictos",
      category: "General",
      paragraphs: [
        "Estos Términos se rigen por la legislación francesa (para usuarios en la Unión Europea) o por las leyes del Estado de Delaware, EE. UU. (para usuarios internacionales), con exclusión de sus normas de conflicto de leyes.",
        "Las partes se comprometen a intentar resolver cualquier controversia de mutuo acuerdo y de buena fe antes de acudir a la vía judicial, contactando a hey@solunary.health.",
      ],
    },
    {
      id: "changes",
      heading: "18. Modificaciones de los términos",
      category: "General",
      paragraphs: [
        "Nos reservamos el derecho de modificar estos Términos para adaptarlos a mejoras del servicio o cambios normativos. La fecha de última actualización en la parte superior reflejará cualquier cambio.",
        "Ante cambios sustanciales, notificaremos con al menos 30 días de antelación por correo electrónico o mediante aviso en la plataforma. El uso continuado tras la fecha de entrada en vigor implica la aceptación de los nuevos Términos.",
      ],
    },
    {
      id: "contact",
      heading: "19. Contacto y Soporte",
      category: "General",
      paragraphs: [
        "Si tienes dudas o consultas sobre estos Términos y Condiciones, puedes contactar con nuestro equipo:",
        "• Correo electrónico: hey@solunary.health",
        "• Plataforma: Solunary Platform, Soporte y Cumplimiento",
        "• Consultas y Seguridad: hey@solunary.health",
      ],
    },
  ],
};
